import {
  BasicUser,
  CreateUser,
  Credentials,
  FullUser,
  UpdateUser,
  UserFavorite,
  UserHolidayDays,
  UserTransform,
} from '../user-types';
import {
  ListSearchOptions,
  SearchFilter,
} from '../../shared/modules/search/search-types';
import {
  ListSearchType,
  ObjectTransform,
} from '../../shared/modules/search/list-search-types';
import useSearch from '../../shared/modules/search/use-search';
import useHttpService from '../../shared/services/http-service';
import { useUserStore } from '../user-store';
import { Role } from '../../permission/roles';
import { Permission } from '../../permission/permissions';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function useUserRepository() {
  const { listSearch } = useSearch();
  const { httpGet, httpPost, httpPut, httpSetConfig, httpDelete } =
    useHttpService();

  const storeUser = (newUser: CreateUser): Promise<BasicUser> => {
    return httpPost<BasicUser>('/users', { ...newUser });
  };

  const updateUser = (userId: number, user: UpdateUser): Promise<void> => {
    return httpPut(`/users/${userId}`, { ...user });
  };

  const destroyUser = (userId: number): Promise<void> => {
    return httpDelete(`users/${userId}`, {});
  };

  const restoreUser = (userId: number): Promise<void> => {
    return httpPost(`users/${userId}/restore`, {});
  };

  const fetchUser = (userId: number): Promise<FullUser> => {
    return httpGet<FullUser>(`/users/${userId}`);
  };

  const fetchUsers = <T extends ObjectTransform = ObjectTransform.BASIC>(
    searchFilter: SearchFilter,
    transform?: T
  ): Promise<UserTransform<T>[]> => {
    const searchOption: ListSearchOptions = {
      type: ListSearchType.USER,
      transform,
    };

    return listSearch<UserTransform<T>[]>({ searchFilter, searchOption });
  };

  const login = async (credentials: Credentials): Promise<any> => {
    const config: any = {
      auth: {
        username: credentials.email,
        password: credentials.password,
      },
    };

    httpSetConfig(config);

    await httpPost('/auth/login', {});
  };

  const logout = async (): Promise<any> => {
    await httpPost('/auth/logout', {} /*, true, null, false*/);

    const userStore = useUserStore();
    userStore.logout();

    return {
      user: null,
    };
  };

  const sendResetPasswordLink = async (email: string): Promise<any> => {
    return await httpPost('/password-reset/send', { email });
  };

  const resetPassword = async (params: any): Promise<any> => {
    const { callback, ...data } = params;
    await httpPost('/password-reset/change-password', data);

    callback();

    return {
      user: null,
    };
  };

  const checkResetPasswordToken = async (token: string): Promise<any> => {
    try {
      await httpPost(`/password-reset/check-token/${token}`, {});
    } catch (error) {
      return false;
    }

    return true;
  };

  const checkLogin = (): Promise<boolean> => {
    return httpGet('/auth/checkLogin');
  };

  const addToFavorites = (userFavorite: UserFavorite): Promise<void> => {
    return httpPost(`/users/add-to-favorites`, { ...userFavorite });
  };

  const removeFromFavorites = (userFavorite: UserFavorite): Promise<void> => {
    return httpDelete<void>(`/users/remove-from-favorites`, {
      data: {
        ...userFavorite,
      },
    });
  };

  const addRole = (userId: number, role: Role): Promise<void> => {
    return httpPost(`/users/${userId}/role`, {
      role,
    });
  };

  const addPermission = (
    userId: number,
    permission: Permission
  ): Promise<void> => {
    return httpPost(`/users/${userId}/permission`, {
      permission,
    });
  };

  const removePermission = (
    userId: number,
    permission: Permission
  ): Promise<void> => {
    return httpDelete(`/users/${userId}/permission`, {
      data: { permission },
    });
  };

  const removeRole = (userId: number, role: Role): Promise<void> => {
    return httpDelete(`/users/${userId}/role`, {
      data: { role },
    });
  };

  const modifyHolidayDays = (
    userDayId: number,
    userHolidayDay: UserHolidayDays
  ): Promise<void> => {
    return httpPut(`/holidays/user-day/${userDayId}`, { ...userHolidayDay });
  };

  const addHolidayUserDay = (userId: number): Promise<void> => {
    return httpPost(`/holidays/${userId}/user-day`, {});
  };

  const addApproverUser = (
    approverId: number,
    userId: number
  ): Promise<void> => {
    return httpPost(`/users/${userId}/holiday-approver`, {
      approvalUserId: approverId,
    });
  };

  const removeApproverUser = (
    approverId: number,
    userId: number
  ): Promise<void> => {
    return httpDelete(`/users/${userId}/holiday-approver`, {
      data: {
        approvalUserId: approverId,
      },
    });
  };

  const addTeamMate = (userId: number, teammateId: number): Promise<void> => {
    return httpPost(`/users/${userId}/team`, {
      userId: teammateId,
    });
  };

  const removeTeamMate = (
    userId: number,
    teammateId: number
  ): Promise<void> => {
    return httpDelete(`/users/${userId}/team`, {
      data: {
        userId: teammateId,
      },
    });
  };

  const addSuperior = (userId: number, superiorId: number): Promise<void> => {
    return httpPost(`/users/${userId}/superiors`, {
      userId: superiorId,
    });
  };

  const removeSuperior = (
    userId: number,
    superiorId: number
  ): Promise<void> => {
    return httpDelete(`/users/${userId}/superiors`, {
      data: {
        userId: superiorId,
      },
    });
  };

  return {
    addToFavorites,
    checkLogin,
    checkResetPasswordToken,
    fetchUsers,
    login,
    logout,
    removeFromFavorites,
    resetPassword,
    sendResetPasswordLink,
    storeUser,
    updateUser,
    fetchUser,
    addRole,
    addPermission,
    restoreUser,
    removeRole,
    removePermission,
    modifyHolidayDays,
    addApproverUser,
    destroyUser,
    removeApproverUser,
    addTeamMate,
    addSuperior,
    removeTeamMate,
    removeSuperior,
    addHolidayUserDay,
  };
}
